import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, Mail, Lock, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const AdminLogin = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, value) => {
    setCredentials(prev => ({ ...prev, [field]: value }));
    setError(''); // Clear error when user starts typing
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      console.log('🔄 Attempting admin login with:', { email: credentials.email });
      
      // Use the AuthContext login method
      const result = await login(credentials);
      
      console.log('🔄 Admin login result:', result);
      
      if (result.success) {
        console.log('✅ Admin login successful, user:', result.user);
        
        // Check if user is admin
        const adminEmails = ['admin@legacyproperty.com', 'lpassociation1@gmail.com'];
        const isAdmin = adminEmails.includes(result.user.email) || result.user.is_admin;
        
        if (!isAdmin) {
          setError('Access denied. Admin credentials required.');
          // Clear the login
          localStorage.removeItem('landlord_token');
          localStorage.removeItem('landlord_user');
          setLoading(false);
          return;
        }
        
        // Navigate to admin dashboard after successful admin login
        navigate('/admin/dashboard');
      } else {
        console.log('❌ Admin login failed:', result.error);
        setError(result.error || 'Login failed. Please check your credentials.');
      }

    } catch (err) {
      console.error('❌ Admin login exception:', err);
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-red-600 p-3 rounded-full shadow-lg">
              <Shield className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white">Admin Access</h1>
          <p className="text-gray-300 mt-2">System Administrator Portal</p>
        </div>

        {/* Login Form */}
        <Card className="shadow-2xl border-gray-700">
          <CardHeader className="bg-gray-50">
            <CardTitle className="text-center text-xl text-gray-900">Admin Sign In</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                  <span className="text-red-800 text-sm">{error}</span>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="flex items-center">
                  <Mail className="h-4 w-4 mr-2" />
                  Admin Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@legacyproperty.com"
                  value={credentials.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required
                  disabled={loading}
                />
              </div>

              <div>
                <Label htmlFor="password" className="flex items-center">
                  <Lock className="h-4 w-4 mr-2" />
                  Admin Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter admin password"
                  value={credentials.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  required
                  disabled={loading}
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-red-600 hover:bg-red-700" 
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Authenticating...
                  </>
                ) : (
                  <>
                    <Shield className="mr-2 h-4 w-4" />
                    Sign In as Admin
                  </>
                )}
              </Button>
            </form>

            {/* Back to regular login */}
            <div className="mt-6 pt-4 border-t border-gray-200 text-center">
              <Button
                type="button"
                variant="link"
                onClick={() => navigate('/owner')}
                className="text-gray-600 hover:text-gray-700 text-sm"
              >
                ← Back to Owner Login
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-400">
            <Shield className="h-3 w-3 inline mr-1" />
            Restricted Access - Authorized Personnel Only
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
